'use client'

import { CaretSortIcon, GearIcon, PlusIcon } from '@radix-ui/react-icons'
import { Team } from '@basehub/data/replicache/index'
import * as React from 'react'
import Link from 'next/link'
import { clsx } from 'clsx'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/sotano-kit/primitives/dropdown-menu'
import { BaseHubAvatar } from '~/components/basehub-avatar'
import { getDefaultAvatarSrc } from '@basehub/data/auth/utils/avatar'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { useToggleState } from '~/hooks/use-toggle-state'
import { Skeleton } from '~/sotano-kit/primitives/skeleton'
import { useUserTeamsList } from '~/hooks-data/team/use-user-teams-list'
import { useUserData } from '~/hooks-data/use-user-data'
import { useTeamCreateFormContext } from './dialog/context'
import {
  type PublicTeam,
  isPublicTeam,
} from '@basehub/data/replicache/mutators/_public-types'

export function TeamDropdownMenu() {
  const { teamSlug } = useDashboardSlugs()
  const menuToggleState = useToggleState()
  const {
    data: { teams, guestTeams },
    isLoading,
  } = useUserTeamsList()
  const { setStep } = useTeamCreateFormContext()
  const {
    personalTeam,
    teamsNotPersonal,
  }: {
    personalTeam: Team | undefined
    teamsNotPersonal: (Team | PublicTeam)[]
  } = React.useMemo(() => {
    let personalTeam: Team | undefined
    const teamsNotPersonal: (Team | PublicTeam)[] = []
    let currentTeam: Team | PublicTeam | undefined
    ;[...teams, ...guestTeams]?.forEach((team) => {
      if (team.slug === teamSlug) {
        currentTeam = team
      }
      if (
        !isPublicTeam(team) &&
        (team.plan === 'personal' || team?.plan_v2?.type === 'personal')
      ) {
        personalTeam = team
      } else teamsNotPersonal.push(team)
    })

    return {
      personalTeam,
      currentTeam,
      teamsNotPersonal: teamsNotPersonal.sort((a, b) => {
        return a.title.localeCompare(b.title)
      }),
    }
  }, [guestTeams, teamSlug, teams])

  if (!isLoading && !personalTeam) return null

  return (
    <div className="flex items-center pl-1">
      <DropdownMenu
        modal={false}
        open={menuToggleState.isOn}
        onOpenChange={menuToggleState.handleToggle}
      >
        <DropdownMenuTrigger
          className="inline-flex py-1.5 items-center align-middle rounded outline-none group focus:outline-dark-control-base focus:outline-1 focus:outline-offset-2 focus:outline hover:bg-dark-border-solid cursor-default"
          style={{
            marginLeft: -2,
            marginRight: -2,
            paddingInline: 4,
            // to make the clickable box bigger we add padding and then remove the extra space with negative margin
          }}
        >
          <CaretSortIcon
            className="flex-1 rounded text-dark-bright"
            width={15}
            height={15}
          />
          {/* Placeholder element (invisible) to fix a bug: when the
          dropdown opens and closes fast, the browser detects a double click
          and tries to select the next available text. not cool. */}
          <span
            className="absolute opacity-0"
            onCopy={(e) => e.preventDefault()}
            aria-hidden
          >
            a
          </span>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          sideOffset={8}
          align="start"
          className="w-[250px] -ml-8"
        >
          <div className="max-h-[320px] overflow-auto">
            <DropdownMenuGroup>
              <DropdownMenuLabel>Personal account</DropdownMenuLabel>
              <Account
                team={personalTeam}
                active={personalTeam?.slug === teamSlug}
                onClose={menuToggleState.handleOff}
              />
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            {Boolean(teamsNotPersonal.length) && (
              <>
                <DropdownMenuGroup>
                  <DropdownMenuLabel className="text-xs leading-4">
                    Teams
                  </DropdownMenuLabel>
                  {teamsNotPersonal.map((team) => {
                    return (
                      <Account
                        key={team.id}
                        team={team}
                        active={team.slug === teamSlug}
                        onClose={menuToggleState.handleOff}
                        guest={isPublicTeam(team)}
                      />
                    )
                  })}
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuGroup>
              <DropdownMenuItem
                className="flex justify-between h-8 text-xs leading-none"
                onSelect={() => {
                  setStep('form')
                  window.history.replaceState(
                    {},
                    '',
                    `${window.location.pathname}?create-team=true`
                  )
                }}
              >
                <span className="flex items-center space-x-2">
                  <div className="flex items-center justify-center size-6 rounded-full bg-dark-control-base">
                    <PlusIcon className="text-white" />
                  </div>
                  <span>Create new team</span>
                </span>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

const Account = ({
  team,
  active,
  guest = false,
  onClose,
}: {
  team: Team | PublicTeam | undefined
  active: boolean
  guest?: boolean
  onClose: () => void
}) => {
  const { data: myUserData } = useUserData(null)

  return (
    <div className="relative">
      <DropdownMenuItem disabled={active || !team} asChild>
        <Link
          href={team ? `/${team.slug}` : '#'}
          className={clsx(
            'flex h-8 justify-between text-xs leading-none data-[disabled]:!opacity-100'
          )}
          style={
            active
              ? {
                  background: `linear-gradient(91deg, rgba(255, 139, 53, 0.30) 0%, rgba(255, 139, 53, 0.00) 100%)`,
                }
              : undefined
          }
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey) {
              return
            }
            e.stopPropagation()

            onClose()
          }}
        >
          {team ? (
            <>
              <span className="flex w-[190px] items-center space-x-2">
                <BaseHubAvatar
                  id={team.id}
                  name={team.title}
                  forceSrc={
                    !isPublicTeam(team) &&
                    (team.plan === 'personal' ||
                      team.plan_v2?.type === 'personal') &&
                    myUserData
                      ? (myUserData.imageUrl ?? undefined)
                      : (team.avatarUrl ?? getDefaultAvatarSrc(team.id))
                  }
                />
                <span className="overflow-hidden overflow-ellipsis whitespace-nowrap">
                  {team.title}
                </span>
              </span>
            </>
          ) : (
            <Skeleton width={'100%'} height={24} />
          )}
        </Link>
      </DropdownMenuItem>
      {team && (
        <>
          {guest ? (
            <span className="text-dark-muted leading-none font-medium px-1.5 py-1 text-xs inline absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none border border-dark-border-solid bg-dark-shade rounded">
              Guest
            </span>
          ) : (
            <Link
              href={`/${team.slug}/settings`}
              className="absolute flex items-center justify-center w-6 h-6 -translate-y-1/2 cursor-default pointer-events-auto transition-color top-1/2 right-2 text-dark-muted hover:text-dark-dim"
              onClick={(e) => {
                e.stopPropagation()
                onClose()
              }}
            >
              <GearIcon width={16} height={16} />
            </Link>
          )}
        </>
      )}
    </div>
  )
}
